import { ChangeDetectionStrategy, Component, ElementRef, Input, ViewChild } from '@angular/core';
import { WizDatasource, WizLabel, WizList } from '@wizeoo/akm-component-lib';
import { WidgetConfigurationItemObj } from '@wizeoo/akm-message-lib';
import { BaselineEngine, FeiKeyDef, FeiVO, KeyPair, LoopResult, WidgetLine, WizConstants } from '@wizeoo/akm-compute-lib';

import { LIST_WIDGET_MODE, WIDGET_ACTION_ORIGIN, WIDGET_GLOBAL_KIND, WIDGET_KIND, WIDGET_STATE } from '../../../model/enums';
import { WidgetBasic } from '../base/widget-basic';
import { ListExtendedPop } from './list-extended.pop';
import { ListWidgetItemRenderer } from './list-widget-item.renderer';
import { IWidget } from '../../../manager/widgets/i-widget';

@Component({
  selector: 'list-widget',
  templateUrl: './list.widget.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})

/** affichage des données en mode liste */
export class ListWidget extends WidgetBasic {

  constructor(elementRef: ElementRef) {
    super(elementRef);
    this.widgetKind = WIDGET_KIND.LIST;
    this.widgetGlobalKind = WIDGET_GLOBAL_KIND.BASELINE;
    this.settingsPopClassFactory = ListExtendedPop;
  }

  /* ##############################################################################
                  composants spécifiques
  ############################################################################## */

  @ViewChild('list') list: WizList; // liste cible
  @ViewChild('measLbl') measLbl: WizLabel; // libellé de la mesure

  /* ##############################################################################
                  paramètres spécifiques
  ############################################################################## */

  public listWidgetItemRenderer: any = ListWidgetItemRenderer; // Renderer de la liste
  @Input() public mode: LIST_WIDGET_MODE = null; // TODO mode de restitution de la liste
  public listDS: WizDatasource<KeyPair> = new WizDatasource<KeyPair>([]) // DS de la liste
    .setSortFields([{ property: 's', way: 'desc', numeric: true }])
    .build();

  public fieldLabel: string = ''; // le libellé du champ


  /* ##############################################################################
                    alimentation des données FEI / construction du grid
  ############################################################################### */

  /**
   * actions jouées sur le drawContent, le alterContent, ou en cas de modifs de filtre, de sélection
   * @override
   * @param origin WIDGET_ACTION_ORIGIN pour cibler qui demande
   * @param data l'objet éventuellement porté
   */
  public drawOrRefresh(origin: WIDGET_ACTION_ORIGIN, data?: any): void {
    
    let mustBuildRows: boolean = false; // on doit construire les lignes
    let mustRefresh: boolean = false; // création mise à jour de la DS de la liste

    // première création
    // if(origin === WIDGET_ACTION_ORIGIN.INIT) {
    //   mustBuildRows = true;
    //   mustRefresh = true;
    // }
    
    // première création
    if(origin === WIDGET_ACTION_ORIGIN.READY) {
      mustBuildRows = true;
      mustRefresh = true;
    }

    // fin de calcul d'un FEI requis pour mes calculs
    if(origin === WIDGET_ACTION_ORIGIN.REQUIRED_COMPUTED) {
      mustBuildRows = true;
      mustRefresh = true;
    }

    // TODO sur configuration, capter les delta filter, colonnes, fields...

    // TODO TODO détecter une modification structurelle en cas 
    // contextDamaged
    // origine: changement de config
    // et mode pivot, sinon les lignes sont toujours OK
    if(origin === WIDGET_ACTION_ORIGIN.CONFIGURE) {   
      mustBuildRows = true;
      mustRefresh = true;
    } 

    // modification du filtre global ... on filtre, et on corrige éventuellement la sélection
    if(origin === WIDGET_ACTION_ORIGIN.GLOBAL_FILTERING) {
      mustBuildRows = true;
      mustRefresh = true;
    }

    // modification du scenario global ...
    if(origin === WIDGET_ACTION_ORIGIN.GLOBAL_SCENARIO_UPDATE) {
      mustBuildRows = true;
      mustRefresh = true;
    }

    if(origin === WIDGET_ACTION_ORIGIN.OUTER_SELECT) {
      // console.log('outer select', this.widgetKind, this.fei.uniqueName, this.state);
      mustBuildRows = true;
      mustRefresh = true;
    }

    if(origin === WIDGET_ACTION_ORIGIN.RECOMPUTE) {
      let loopResults: Map<FeiVO, LoopResult> = data[0];  
      if(this.fei.scenario === WizConstants.SCEN_KEY_CODE) {
        [...loopResults.keys()].forEach((fei: FeiVO) => {
          if(fei.visionStepLevel === this.fei.visionStepLevel) {
            if(loopResults.get(fei).created.size > 0 || loopResults.get(fei).deleted.size > 0) {
              mustBuildRows = true;
              mustRefresh = true;
            } else {
              mustRefresh = true;
            }
          }
        });
      }
      
      // gestion multiscen... prendre tous les loopers du même niveau
      let loop: LoopResult = loopResults.get(this.fei);
      if(loop?.created.size > 0 || loop?.deleted.size > 0) {
        mustBuildRows = true;
        mustRefresh = true;
      } else {
        mustRefresh = true;
      }
      // console.log('WIDGET_ACTION_ORIGIN.RECOMPUTE', this.fei.uniqueName, mustBuildRows, mustRefresh);
    }

    if(mustBuildRows) {
      this.buildLines(); // délégué au WidgetBasic
    }

    if(mustRefresh) {
      this.mainDS.refresh();
      this.buildListLines();
    }
  }

  /**
   * construction 'initiale' des objets graphiques du widget :
   * - sur init véritable
   * - sur modification de la datasource
   * - sur modification de scenario (modifie la DS)
   */
  public drawContent(): void {
    
    let master: IWidget = this.model.widgetEngine.checkFirstMaster(this); // je cherche un master. S'il y en a un, READY n'est pas suffisant
    // si j'ai un master et qu'il n'est pas prêt, on ne fait rien
    if (!!master && master.state !== WIDGET_STATE.SELECTION_COMPLETE) { // j'ai un master, mais il n'est pas prêt
      // console.log('checkFeiState master not ready', this.widgetKind, this.fei.uniqueName);
      return;
    }

    if(this.state >= WIDGET_STATE.READY) { // déjà construit, plus rien a faire
      return;
    }

    this.drawOrRefresh(WIDGET_ACTION_ORIGIN.INIT); // actions communes draw / alter
    super.drawContent(); // pas au début, c'est lui qui dispatch les events
  }

  /**
   * reconstruction des objets graphiques du widget sur changement de structure : 
   * - modification des filtres qui impactent les colonnes
   * - changement de champs en ligne / colonnes / data
   * - changement de filtre
   * - changement de sélection
   * @param origin 
   */
  public alterContent(origin: WIDGET_ACTION_ORIGIN): void {
    this.drawOrRefresh(origin);
    super.alterContent(origin);
  }



  /**
   * construction des items de la liste
   */
  public buildListLines(): void {
    if (!!this.context.r && this.context.r.length > 0 && !!this.context.d && this.context.d.length === 1) {
      this.fieldLabel = this.context.d[0].cod; // TODO Trad

      const fkd: FeiKeyDef = BaselineEngine.getKey(this.fei, this.context.d[0].cod); // conf de la mesure
      const aggMethod: string = this.context.d[0].agg; // méthode d'aggrégat
      const format: string = this.context.d[0].fmt; // format de la mesure
      const tmpRes: KeyPair[] = []; // collection de destination

      let lbl: string = this.context.d[0]?.lbl || fkd.fullCode;
      this.measLbl.wizLabel = lbl;

      this.mainDS.getWorkedSource()?.forEach((wl: WidgetLine) => {
        const itemLabel: string[] = [];
        this.context.r.forEach((wcoi: WidgetConfigurationItemObj) => {
          itemLabel.push(BaselineEngine.getLabel(this.fei, wl, wcoi.cod, wcoi.fdvf || WizConstants.FIELD_LABEL));
        });
        const realValue: string =  this.model.aggregateEngine.call(aggMethod, this.fei, [...wl.ch.values()], fkd);
          
        const formattedValue: string = this.model.formatEngine.applyFormat(
          realValue,
          fkd.datakind,
          this.context.d[0].fmt,
          this.context.d[0].agg
        );
        tmpRes.push({r: formattedValue, l:itemLabel.join(' - '), s: Number(realValue) });   
        tmpRes.sort((a, b) => a.s - b.s); // on tri
      });

      this.listDS.setSource(tmpRes);
    } else {
      this.listDS.setSource([]);
    }
    
    this.listDS.refresh(); // refresh des values modifiées
  }

  /* ##############################################################################
                    Tools
     ############################################################################## */

  /** demande d'export depuis le bouton */
  public exportDatas(): void {

    const rawArr: string[][] = this.listDS.getSource().map((s) => [s.l, s.r]);

    let lbl: string = this.fieldLabel;
    try {
      if (this.context.d[0].lbl) {
        lbl = this.context.d[0].lbl;
      }
    } catch(e: any) {

    }
    this.model.excelExportEngine.exportAsExcelFromList('allkeyme', rawArr, lbl);
  }
}
